import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/utils/api";

const Sidebar = ({ active }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/admin/login");
  };

  const menuItems = [
    { id: "dashboard", label: "Dashboard", path: "/admin/dashboard" },
    { id: "workers", label: "Workers", path: "/admin/workers" },
    { id: "courses", label: "Courses", path: "/admin/courses" },
    { id: "analytics", label: "Analytics", path: "/admin/analytics" },
    { id: "settings", label: "Settings", path: "/admin/settings" },
  ];

  return (
    <div className="admin-sidebar">
      <div className="p-6 border-b border-slate-700">
        <h1 className="text-xl font-bold">Training Admin</h1>
        <p className="text-sm text-slate-400 mt-1">{user.full_name}</p>
      </div>
      <nav className="p-4">
        {menuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => navigate(item.path)}
            className={`w-full flex items-center px-4 py-3 rounded-lg mb-2 transition-colors text-left ${
              active === item.id
                ? "bg-[#2e7fd4] text-white"
                : "text-slate-300 hover:bg-slate-700"
            }`}
          >
            {item.label}
          </button>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full text-slate-300 hover:bg-slate-700 hover:text-white"
        >
          Logout
        </Button>
      </div>
    </div>
  );
};

export default function Settings() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [client, setClient] = useState(null);
  const [brandColor, setBrandColor] = useState("#0a2240");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClient();
  }, []);

  const fetchClient = async () => {
    try {
      if (user.client_id) {
        const response = await api.get(`/clients/${user.client_id}`);
        setClient(response.data);
        setBrandColor(response.data.brand_color);
      }
    } catch (error) {
      console.error("Failed to fetch client", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBrandColor = async () => {
    try {
      await api.patch(`/clients/${user.client_id}`, {
        brand_color: brandColor,
      });
      toast.success("Brand color updated successfully");
      fetchClient();
    } catch (error) {
      toast.error("Failed to update brand color");
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar active="settings" />
        <div className="admin-content flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#2e7fd4] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar active="settings" />
      <div className="admin-content">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Settings</h1>

        {/* Branding Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Branding</h2>
          
          <div className="max-w-md space-y-6">
            <div>
              <Label htmlFor="client_name">Organization Name</Label>
              <Input
                id="client_name"
                value={client?.name || ""}
                disabled
                className="bg-slate-50"
              />
            </div>

            <div>
              <Label htmlFor="brand_color">Brand Color</Label>
              <div className="flex gap-4 items-center">
                <Input
                  id="brand_color"
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="h-12 w-20"
                />
                <Input
                  type="text"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={handleUpdateBrandColor}
                  className="bg-[#0a2240] hover:bg-[#0f325c] text-white"
                >
                  Update
                </Button>
              </div>
              <p className="text-sm text-slate-600 mt-2">
                This color will be used in certificates and worker portal
              </p>
            </div>

            <div>
              <Label htmlFor="logo">Logo Upload</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                <svg className="w-12 h-12 mx-auto text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-slate-600 mb-2">Logo upload coming soon</p>
                <p className="text-sm text-slate-500">PNG, JPG up to 2MB</p>
              </div>
            </div>
          </div>
        </div>

        {/* Account Section */}
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Account</h2>
          
          <div className="max-w-md space-y-4">
            <div>
              <Label>Email</Label>
              <Input value={user.email} disabled className="bg-slate-50" />
            </div>
            <div>
              <Label>Role</Label>
              <Input value="Client Admin" disabled className="bg-slate-50" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
